import Component from 'flarum/common/Component';
/** Resolve a hero image setting (URL string or { url } object) to a URL. */
export function heroImageUrl(value) {
  if (!value) return null;
  const url = typeof value === 'string' ? value : value.url;
  // It lands in a CSS url(): nothing that could close it, and no script scheme.
  if (typeof url !== 'string' || !url || /["'()\\\s<>]/.test(url)) return null;
  return /^(https?:)?\/\/|^\//i.test(url) ? url : null;
}

/** Background CSS for the colored part of a hero (gradient / solid / image). */
function backgroundStyle(s) {
  const style = {};
  const c1 = hexColor(s.bg_color) || '#4f46e5';
  if (s.bg_type === 'solid') {
    style.background = c1;
  } else if (s.bg_type === 'image' && s.layout !== 'split') {
    const url = heroImageUrl(s.bg_image);
    const o = Math.max(0, Math.min(Number(s.overlay ?? 35), 80)) / 100;
    if (url) {
      style.backgroundImage = `linear-gradient(rgba(0,0,0,${o}),rgba(0,0,0,${o})), url(${url})`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
    } else {
      style.background = c1;
    }
  } else {
    const c2 = hexColor(s.bg_color2) || '#9333ea';
    const angle = Number(s.bg_angle ?? 135);
    style.background = `linear-gradient(${angle}deg, ${c1}, ${c2})`;
  }
  return style;
}

/** A #rgb / #rrggbb colour, or null for anything else. */
function hexColor(v) {
  const c = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(c) ? c : null;
}
function safeUrl(url) {
  return /^(https?:\/\/|\/)/i.test(url || '') ? url : null;
}

/**
 * A reusable hero banner. Two layouts:
 *  - "full": full-bleed background (gradient / solid / image + overlay), centered
 *    or left-aligned content.
 *  - "split": side-by-side — colored content panel beside an image panel.
 * Used by the Hero page block and by content-type listing pages.
 */
export default class HeroBanner extends Component {
  view() {
    const s = this.attrs.settings || {};
    return s.layout === 'split' ? this.renderSplit(s) : this.renderFull(s);
  }
  buttons(s, style) {
    const b1 = safeUrl(s.button_url);
    const b2 = safeUrl(s.button2_url);
    if (!(s.button_label && b1) && !(s.button2_label && b2)) return null;

    // Button style: "mixed" = primary filled + secondary outline (default);
    // "outline" = both outline; "filled" = both filled.
    const bs = ['outline', 'filled'].includes(s.button_style) ? s.button_style : 'mixed';
    const filled = 'Button Button--primary PB-Hero-button';
    const outline = 'Button PB-Hero-button PB-Hero-button--secondary';
    const primaryClass = bs === 'outline' ? outline : filled;
    const secondaryClass = bs === 'filled' ? filled : outline;
    return m("div", {
      className: "PB-Hero-buttons",
      style: style
    }, s.button_label && b1 && m("a", {
      className: primaryClass,
      href: b1
    }, s.button_label), s.button2_label && b2 && m("a", {
      className: secondaryClass,
      href: b2
    }, s.button2_label));
  }

  /**
   * The text colour: the heading / text colour pair (the same choice sections
   * have) wins, then the older single "custom" colour, then the light/dark
   * preset in the stylesheet. Only a #hex ever reaches the style attribute.
   */
  customColor(s, which = 'body') {
    const legacy = s.text_color === 'custom' ? hexColor(s.text_color_custom) || '#ffffff' : null;
    const own = hexColor(which === 'head' ? s.head_color : s.body_color);
    const c = own || legacy;
    return c ? {
      color: c
    } : undefined;
  }
  body(s, includeButtons = true) {
    const colorStyle = this.customColor(s);
    return m("div", {
      className: "PB-Hero-inner",
      style: colorStyle
    }, s.heading && m("h1", {
      className: "PB-Hero-heading",
      style: this.customColor(s, 'head')
    }, s.heading), s.subheading && m("p", {
      className: "PB-Hero-subheading"
    }, s.subheading), includeButtons && this.buttons(s));
  }
  renderFull(s) {
    const align = s.align === 'left' ? 'left' : 'center';
    const height = ['sm', 'md', 'lg'].includes(s.height) ? s.height : 'md';
    const dark = s.text_color === 'dark';
    // Bottom-pinned buttons must be a DIRECT child of the hero (not the inner,
    // which is position:relative and would anchor them mid-hero), so render them
    // outside the inner in that mode.
    const bottom = s.buttons_position === 'bottom';
    const btnsBottom = bottom ? ' PB-Hero--btnsBottom' : '';
    return m("div", {
      className: `PB-Hero PB-Hero--full PB-Hero--${height} PB-Hero--${align} ${dark ? 'PB-Hero--dark' : 'PB-Hero--light'}${btnsBottom}`,
      style: backgroundStyle(s)
    }, this.body(s, !bottom), bottom && this.buttons(s, this.customColor(s)));
  }
  renderSplit(s) {
    const height = ['sm', 'md', 'lg'].includes(s.height) ? s.height : 'md';
    const dark = s.text_color === 'dark';
    const imageRight = s.image_side !== 'left';
    const img = heroImageUrl(s.bg_image);
    const content = m("div", {
      className: "PB-Hero-col PB-Hero-content",
      style: backgroundStyle(s)
    }, this.body(s));
    const media = m("div", {
      className: `PB-Hero-col PB-Hero-media ${img ? '' : 'is-empty'}`,
      style: img ? {
        backgroundImage: `url(${img})`
      } : undefined
    }, !img && m("i", {
      className: "fas fa-image"
    }));
    return m("div", {
      className: `PB-Hero PB-Hero--split PB-Hero--${height} ${dark ? 'PB-Hero--dark' : 'PB-Hero--light'}`
    }, imageRight ? [content, media] : [media, content]);
  }
}
flarum.reg.add('ernestdefoe-page-builder', 'forum/components/HeroBanner', HeroBanner);